import { Box, Button, IconButton, Typography, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import CustomMarkdown from '../../CustomMarkdown'
import { Icons } from '../../../../../shared/utils/icons.jsx'
import { markdownToText } from '../../../../../shared/utils/helpers.jsx'
import DataLoadingDots from '../../../../../shared/components/common/DataLoadingDots'

const AiResponse = ( { } ) => {

    const [listeningPaused, setListeningPaused] = useState( false )
    const [listening, setListening] = useState( false )

    const { layout, border, palette, shadows } = useTheme()
    const SimbaAIStore = useSimbaAIStore()

    const aiMentorArea = useRef()

    const fetchAnswerFromAI = async ( topicDetails ) => {
        if ( SimbaAIStore.getAiResponsesQueue.request ) {
            SimbaAIStore.addResponse( { topic: SimbaAIStore.getAiResponsesQueue.request, response: SimbaAIStore.getAiResponsesQueue.response } )
            SimbaAIStore.setAiResponsesQueue( { request: "", response: "" } )
        }
        try {
            SimbaAIStore.setView( 'fetching-simba-ai-response', "left", false )
            if ( await SimbaAIStore.getAIResponse( topicDetails ) )
                SimbaAIStore.setView( 'simba-ai-response', "left", false )
            else
                SimbaAIStore.setView( 'simba-ai-response-error', "left", false )

        } catch ( err ) {
            console.log( err )
        }
    }

    const changeSubject = () => {
        SimbaAIStore.setView( 'home', "left", false )
    }

    const listenToResponse = () => {
        if ( !listening ) {
            if ( speechSynthesis.speaking )
                speechSynthesis.cancel()
            const speechText = markdownToText( SimbaAIStore.getAiResponse )
            const utterance = new SpeechSynthesisUtterance( speechText )
            utterance.onpause = () => setListeningPaused( true )
            utterance.onresume = () => setListeningPaused( false )
            utterance.onend = () => setListening( false )
            utterance.onerror = () => setListening( false )
            const voices = speechSynthesis.getVoices()
            utterance.voice = voices[0]
            speechSynthesis.speak( utterance )
            setListening( true )
        }
    }

    const toggleListening = () => {
        if ( !speechSynthesis.paused ) {
            speechSynthesis.pause()
        } else {
            speechSynthesis.resume()
        }
    }

    const stopListening = () => {
        speechSynthesis.cancel()
        setListening( false )
    }

    const onTypingCompleted = () => {
        SimbaAIStore.setView( 'completed-typing-simba-ai-response', "left", false )
        if ( aiMentorArea.current )
            setTimeout( () => {
                const scrollContainer = aiMentorArea.current
                if ( scrollContainer.scrollTop + scrollContainer.clientHeight < scrollContainer.scrollHeight ) {
                    scrollContainer.scrollTop = scrollContainer.scrollHeight
                }
            }, 50 )
    }

    useEffect( () => {
        const stopListening = () => {
            speechSynthesis.cancel()
            setListening( false )
        }
        return () => stopListening()
    }, [] )

    return (
        <Box
            ref={aiMentorArea}
            sx={{
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                flexGrow: 1
            }}>
            {SimbaAIStore.getView.current === "fetching-simba-ai-response" && <DataLoadingDots padding="20px" waitingMessage='Please wait Simba is generating your response...' />}
            {["completed-typing-simba-ai-response", "simba-ai-response"].includes( SimbaAIStore.getView.current ) && <Box
                sx={{
                    flexGrow: 1,
                    overflow: "initial",
                    display: "flex",
                    flexDirection: "column"
                }}>
                <Box
                    sx={{
                        borderRadius: "20px",
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        border: border[1],
                        margin: "20px"
                    }}>
                    <Box
                        sx={{
                            padding: "10px",
                            flexWrap: "wrap",
                            gap: layout.cardsGap,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: border[1]
                        }}>
                        <Box>
                            <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                    fontSize: "16px",
                                    fontWeight: "200"
                                }}>Response for the request <strong>Explain {SimbaAIStore.getCurrentTopic?.topic}</strong></Typography>
                            <Typography variant="subtitle2" color="GrayText" sx={{
                                marginTop: "-5px"
                            }}>From: <em>{SimbaAIStore.getCurrentTopic?.subject_name}, {SimbaAIStore.getCurrentTopic?.module}</em></Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                padding: "10px 20px",
                                borderRadius: "10px",
                                bgcolor: palette.secondary.main,
                                gap: "10px",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer"
                            }}>
                            <Box
                                onClick={listenToResponse}
                                sx={{
                                    color: palette.secondary.contrastText,
                                    display: "flex",
                                    gap: "10px",
                                    alignItems: "center"
                                }}>
                                {listening ? Icons.small.GraphicEqRounded : Icons.small.VolumeUpIcon}
                                <Typography variant="subtitle2" color={palette.secondary.contrastText} >{listening ? "Listening" : "Listen"}</Typography>
                            </Box>
                            {listening && <IconButton onClick={toggleListening} sx={{ color: palette.secondary.contrastText }} size="small">{listeningPaused ? Icons.default.PlayArrowIcon : Icons.default.PauseIcon}</IconButton>}
                            {listening && <IconButton onClick={stopListening} sx={{ color: palette.secondary.contrastText }} size="small">{Icons.default.Stop}</IconButton>}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            flexGrow: 1,
                            overflow: "auto",
                            padding: "20px"
                        }}>
                        <CustomMarkdown scrollToBottom scrollContainer={aiMentorArea} typeWriterEffect onTypingCompleted={onTypingCompleted} content={SimbaAIStore.getAiResponse || ""} speed={80} />
                    </Box>
                </Box>

                {["completed-typing-simba-ai-response"].includes( SimbaAIStore.getView.current ) && SimbaAIStore.getSuggestedTopics?.topics?.length > 0 && <Box
                    sx={{
                        display: "flex",
                        border: border[1],
                        borderRadius: layout.borderRadius,
                        margin: "20px",
                        flexDirection: "column",
                        gap: "10px"
                    }}>
                    <Box
                        sx={{
                            display: "flex",
                            gap: "20px",
                            justifyContent: "space-between",
                            bgcolor: palette.contentBg,
                            padding: "10px"
                        }}>
                        <Box>
                            <Typography variant="h6" component="p" sx={{
                                fontSize: "16px"
                            }}>{SimbaAIStore.getSuggestedTopics.topicsCategory?.name} topics</Typography>
                            <Typography
                                variant="subtitle2"
                                color="GrayText"
                                sx={{
                                    marginTop: "-5px",
                                    fontSize: "12px"
                                }}><em>Select the next topic to learn.</em></Typography>
                        </Box>
                        <Button onClick={changeSubject} size='small' variant='contained' disableElevation color='secondary' startIcon={Icons.default.Sync} sx={{ textTransform: "capitalize", display: "flex" }} >{SimbaAIStore.getSuggestedTopics.topicsCategory?.category === "recentTopics" ? "View Subjects" : "Switch subject"}</Button>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            padding: "10px",
                            gridTemplateColumns: { md: "repeat(3,1fr)", xs: "1fr", sm: "1fr 1fr" },
                            flexWrap: "wrap",
                            width: "100%",
                            gap: layout.cardsGap
                        }}>
                        {SimbaAIStore.getSuggestedTopics?.topics?.map( ( topicDetails ) => {
                            return (
                                <Box
                                    component="button"
                                    onClick={() => fetchAnswerFromAI( topicDetails )}
                                    className="animation-wave"
                                    key={topicDetails.topic}
                                    sx={{
                                        flexGrow: 1,
                                        overflow: "hidden",
                                        padding: "10px 20px",
                                        borderRadius: "10px",
                                        boxShadow: shadows[1],
                                        border: border[1],
                                        borderBottom: `4px solid ${palette.borderColor}`,
                                        position: "relative",
                                        bgcolor: palette.contentBg,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        cursor: "pointer",
                                        transition: "200ms",

                                        ":hover": {
                                            borderColor: palette.primary.light,
                                            transform: `skew(-5deg)`
                                        }
                                    }}>
                                    <Typography title={topicDetails.topic} noWrap color='textPrimary' sx={{
                                        fontSize: "16px"
                                    }} >{topicDetails.topic}</Typography>
                                    <Typography
                                        title={topicDetails.subject_name}
                                        noWrap
                                        color='textSecondary'
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            fontSize: "12px"
                                        }}>{topicDetails.subject_name}</Typography>
                                </Box>
                            );
                        } )}
                    </Box>
                </Box>}

            </Box>}

        </Box>
    );
}

export default AiResponse
